import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { addMinutes, format } from 'date-fns';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import Booking from '../../../../core/types/model/Booking';
import EntityName from '../../../../core/enums/EntityName';
import { DATE_FORMAT_HOURS_MINUTES, DATE_FORMAT_ISO, TIME_FORMAT_BACKEND } from '../../../../core/constants/date';
import { BookingModalType } from '../../../../core/state/bookings/types';
import { formatDate, getTimeDate } from '../../../../core/utils/date';
import { isBookingDateValid } from '../../../../core/utils/isBookingDateValid';
import { useValidationNotification } from '../../hooks';
import { RootState } from '../../../../core/state/state';
import { Actions as BookingActions } from '../../../../core/state/bookings/actions';
import { selectProjectSettings } from '../../../../core/state/bookingProjectSettings/selectors';
import { generateEntityCreateAsyncAction } from '../../../../core/state/entity/generateEntityAsyncActions';
import { spawnGlobalNotification } from '../../../../core/state/global-notifications/asyncActions';
import { GlobalNotificationTypes } from '../../../../core/enums/GlobalNotificationTypes';

import Modal from '../../../components/modal/Modal';
import Button from '../../../../components/form/button/Button';
import Input from '../../../../components/form/input/Input';
import TextArea from '../../../../components/form/text-area/TextArea';
import TransportDeliveryZone from './transport-delivery-zone/TransportDeliveryZone';
import { isMobileScreenSize } from '../../../../core/utils/isMobileScreenSize';
import Select from '../../../../components/form/select/Select';
import { generateTimeOptions } from '../../../../components/form/range-time-picker/time-picker/TimePicker';
import { editBooking, editPendingBooking } from '../../../../core/state/bookings/asyncActions';
import { BookingStatus } from '../../../../core/enums/BookingStatus';
import { contactsToOptions } from '../../helpers';
import { getExternalUserContacts } from '../../../../core/state/subcontractors/asyncActions';
import { CONTACT_NAME_REGEX } from '../../../../core/constants/regex';

import './ManageBookingInternalModal.scss';

interface Props {
  openSendEmailModal: (id: string) => void;
}

type CreateBookingInternalFormData = {
  additionalTitle: string;
  date: string;
  startTime: string;
  endTime: string;
  contactPersonText: string;
  transportEquipmentId: number | string;
  deliveryZoneId: number | string;
  description: string;
  title: string;
  confirmationStatus: BookingStatus;
  bookedForConstructorId?: number;
  contactPersonId?: string;
};

const convertTimeToNumber = (time: string) => {
  const formatedTime = Number(time.replace(':', ''));
  return formatedTime;
};

const ManageBookingInternalModal: React.FC<Props> = ({ openSendEmailModal }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let validationSchema = yup
    .object()
    .shape({
      date: yup.date().min(today, t('projectSettings.blockBookingSlots.form.option.dateIsInPast')).required(),
      startTime: yup.string().required(),
      endTime: yup.string().required(),
      contactPersonText: yup.string(),
      contactPersonId: yup.string(),
      transportEquipmentId: yup.number().required(),
      deliveryZoneId: yup.number().required(),
      description: yup.string().required()
    })
    .required();
  validationSchema = validationSchema.test('endTime', t('booking.deliveryTime.error'), (obj: CreateBookingInternalFormData) => {
    if (convertTimeToNumber(obj.startTime) >= convertTimeToNumber(obj.endTime)) {
      return new yup.ValidationError(t('booking.deliveryTime.error'), null, 'endTime');
    }
    return true;
  });

  validationSchema = validationSchema.test('startTime', t('booking.deliveryTime.error'), (obj: CreateBookingInternalFormData) => {
    if (convertTimeToNumber(obj.startTime) >= convertTimeToNumber(obj.endTime)) {
      return new yup.ValidationError(t('booking.deliveryTime.error'), null, 'startTime');
    }
    return true;
  });
  const isBookingEditModalOpen = useSelector((state: RootState) => state.bookings.editModal);

  const bookingState = useSelector((state: RootState) => state.bookings);
  const bookingId = useSelector((state: RootState) => state.bookings.selectedBooking?.id);
  const { isOpen: isAnyModalOpen, data } = useSelector((state: RootState) => state.bookings.modal);
  const isOpen = (isAnyModalOpen && data?.type === BookingModalType.Internal) || isBookingEditModalOpen;
  const [isBookingDeadlineError, setIsBookingDeadlineError] = useState(false);
  const [isOperatingHoursError, setIsOperatingHoursError] = useState(false);
  const [isTimeAndZoneError, setIsTimeAndZoneError] = useState(false);
  const isPendingBooking = bookingState.selectedBooking?.confirmationStatus === BookingStatus.Pending;
  const selectedBooking = useSelector((state: RootState) => state.bookings.selectedBooking);

  useEffect(() => {
    const constructorId = selectedBooking?.bookedForConstructorId;
    if (constructorId) {
      dispatch(getExternalUserContacts(Number(constructorId)));
    }
  }, [dispatch, selectedBooking?.bookedForConstructorId]);

  const contacts = useSelector((state: RootState) => state.subcontractor.contacts);
  const contactOptions = useMemo(() => contacts?.map(contactsToOptions) ?? [], [contacts]);

  const extractName = (str?: string) => str?.match(CONTACT_NAME_REGEX)?.[1];

  const matchedContactOption = useMemo(() => {
    const cotactName = extractName(bookingState.selectedBooking?.contactPersonText ?? '');
    return contactOptions.find((option) => extractName(option.text) === cotactName);
  }, [bookingState.selectedBooking?.contactPersonText, contactOptions]);

  const initialValues: Partial<CreateBookingInternalFormData> = useMemo(() => {
    if (isBookingEditModalOpen) {
      const selectedBookingData = bookingState.selectedBooking;
      const additionalTitle = selectedBookingData?.title.split('Köster:')[0].trim();
      const confirmationStatus = bookingState?.selectedBooking?.confirmationStatus;
      return {
        title: additionalTitle,
        date: selectedBookingData?.date ?? '',
        startTime: format(getTimeDate(selectedBookingData?.startTime), DATE_FORMAT_HOURS_MINUTES) ?? '',
        endTime: format(getTimeDate(selectedBookingData?.endTime), DATE_FORMAT_HOURS_MINUTES) ?? '',
        contactPersonText: selectedBookingData?.contactPersonText ?? '',
        transportEquipmentId: selectedBookingData?.transportEquipmentId ?? '',
        deliveryZoneId: selectedBookingData?.deliveryZoneId ?? '',
        description: selectedBookingData?.description ?? '',
        confirmationStatus: confirmationStatus ?? undefined,
        contactPersonId: matchedContactOption?.value ?? undefined
      };
    }
    return data?.initialValues ?? {};
  }, [isBookingEditModalOpen, data?.initialValues, bookingState.selectedBooking, matchedContactOption?.value]);

  const isMobileScreen = isMobileScreenSize();

  const projectSettings = useSelector(selectProjectSettings);
  const { id: projectId, projectDeliveryZones: deliveryZones, bookingProjectEquipment: transportEquipment } = projectSettings;
  const { operatingHoursFrom, operatingHoursTo } = projectSettings.bookingProjectSettings;

  const userName = useSelector((state: RootState) => state.auth.authInfo?.account.name ?? '');
  const {
    handleSubmit,
    control,
    setValue,
    reset,
    clearErrors,
    formState: { isValid, isSubmitted, errors }
  } = useForm<CreateBookingInternalFormData>({
    mode: 'onChange',
    defaultValues: {
      additionalTitle: initialValues?.title ?? '',
      date: initialValues.date ?? '',
      startTime: initialValues?.startTime ?? '',
      endTime: initialValues?.endTime ?? '',
      contactPersonText: initialValues?.contactPersonText ?? '',
      transportEquipmentId: initialValues?.transportEquipmentId ?? '',
      deliveryZoneId: initialValues?.deliveryZoneId ?? '',
      description: initialValues?.description ?? '',
      confirmationStatus: initialValues.confirmationStatus ?? undefined,
      contactPersonId: initialValues.contactPersonId ?? undefined
    },
    resolver: yupResolver(validationSchema)
  });

  const disableBeforeValue = operatingHoursFrom;
  const disableAfterValue = operatingHoursTo;

  const timeToOption = (item: { date: Date }) => ({
    value: format(item.date, DATE_FORMAT_HOURS_MINUTES),
    text: format(item.date, DATE_FORMAT_HOURS_MINUTES)
  });

  const timeOptions = useMemo(
    () =>
      generateTimeOptions(disableBeforeValue, disableAfterValue)
        ?.filter((item) => !item.isDisabled)
        ?.map(timeToOption),
    [disableBeforeValue, disableAfterValue]
  );

  const closeModal = useCallback(() => {
    dispatch(BookingActions.closeModal());
    dispatch(BookingActions.closeEditModal());
  }, [dispatch]);

  const onSubmit = useCallback(
    async (formData: CreateBookingInternalFormData) => {
      if (!isBookingDateValid(projectSettings.bookingProjectSettings.bookingDeadline, formData.date, dispatch, t)) {
        setIsBookingDeadlineError(true);
        return;
      }
      setIsBookingDeadlineError(false);
      const { additionalTitle, ...rest } = formData;
      const createBooking = generateEntityCreateAsyncAction(EntityName.Booking);

      const dispatchNotification = () => {
        dispatch(
          spawnGlobalNotification({
            type: GlobalNotificationTypes.Success,
            title: t('booking.created.success.title'),
            message: t(`booking.${isBookingEditModalOpen ? 'updated' : 'created'}.success.text`),
            delay: 5000
          })
        );
      };

      const title = isPendingBooking ? selectedBooking?.title : `${additionalTitle || ''} Köster: ${userName}`;

      const booking = {
        ...rest,
        projectId,
        title,
        type: 'INTERNAL',
        date: formatDate(new Date(formData.date), DATE_FORMAT_ISO),
        contactPersonId: Number(formData.contactPersonId),
        contactPersonText: formData.contactPersonText,
        startTime: format(getTimeDate(formData?.startTime), TIME_FORMAT_BACKEND),
        endTime: format(getTimeDate(formData?.endTime), TIME_FORMAT_BACKEND),
        confirmationStatus: isPendingBooking ? BookingStatus.Open : formData.confirmationStatus
      } as Booking;

      if (isBookingEditModalOpen && bookingId) {
        const response: string = await dispatch<any>(
          isPendingBooking ? editPendingBooking(bookingId, booking as Partial<Booking>) : editBooking(bookingId, booking as Partial<Booking>)
        );
        if (response === 'startTime') {
          setIsTimeAndZoneError(true);
          dispatch(
            spawnGlobalNotification({
              type: GlobalNotificationTypes.Error,
              title: t('booking.validation.timeSlot.title'),
              message: t('booking.validation.timeSlot.message')
            })
          );
        } else {
          dispatch(BookingActions.openDetails(booking));
          dispatchNotification();
          if (!isPendingBooking) {
            openSendEmailModal(bookingId.toString());
          }
        }
      } else {
        const response: string = await dispatch<any>(createBooking(booking as Partial<Booking>));
        if (response === 'NotValidOperatingHours' || response === 'startTime' || response === 'date' || response === 'NotValidDeliveryRange') {
          setIsOperatingHoursError(true);
        } else {
          dispatchNotification();
          setIsOperatingHoursError(false);
          openSendEmailModal(response);
        }
      }
    },
    [
      projectSettings.bookingProjectSettings.bookingDeadline,
      dispatch,
      t,
      isPendingBooking,
      selectedBooking?.title,
      userName,
      projectId,
      isBookingEditModalOpen,
      bookingId,
      openSendEmailModal
    ]
  );

  const errorInputName = useValidationNotification(isOpen, isSubmitted);

  // Reset form when opened and closed
  useEffect(() => {
    if (isOpen) {
      reset({
        additionalTitle: initialValues?.title ?? '',
        date: initialValues?.date ?? '',
        startTime: initialValues?.startTime ?? '',
        endTime: initialValues?.endTime ?? formatDate(addMinutes(getTimeDate(operatingHoursFrom), 30), TIME_FORMAT_BACKEND),
        contactPersonText: initialValues?.contactPersonText ?? '',
        transportEquipmentId: initialValues?.transportEquipmentId ?? '',
        deliveryZoneId: initialValues?.deliveryZoneId ?? '',
        description: initialValues?.description ?? '',
        confirmationStatus: initialValues.confirmationStatus ?? undefined,
        contactPersonId: initialValues.contactPersonId ?? undefined
      });
    }
  }, [isOpen, reset, initialValues, operatingHoursFrom]);

  useEffect(() => {
    if (isSubmitted && !isBookingDeadlineError && !isOperatingHoursError && !isTimeAndZoneError) {
      closeModal();
    }
  }, [closeModal, isSubmitted, isBookingDeadlineError, isOperatingHoursError, dispatch, isTimeAndZoneError]);

  const title = <h3 className="modal-title"> {t(isBookingEditModalOpen ? 'common.editBooking' : 'booking.create.heading')}</h3>;
  const buttons = (
    <div className="create-booking-modal__buttons">
      <Button buttonTypes={!isMobileScreen ? ['transparent'] : ['blue-outline', 'block']} onClick={closeModal}>
        {t('common.cancel')}
      </Button>
      {isPendingBooking ? (
        <Button buttonTypes={!isMobileScreen ? ['blue'] : ['blue', 'block']} type="submit" form="create-booking-form" disabled={!isValid}>
          {t('booking.modal.saveAndConfirm')}
        </Button>
      ) : (
        <Button buttonTypes={!isMobileScreen ? ['blue'] : ['blue', 'block']} type="submit" form="create-booking-form" disabled={!isValid}>
          {t(isBookingEditModalOpen ? 'common.update' : 'common.create')}
        </Button>
      )}
    </div>
  );

  if (!isOpen) {
    return <></>;
  }

  return (
    <Modal isFullScreen={isMobileScreen} isOpen={isOpen} closeModal={closeModal} title={title} toolbar={buttons} size="medium" height={585}>
      <form id="create-booking-form" className="create-booking-modal" onSubmit={handleSubmit(onSubmit)}>
        <div className={`create-booking-modal__content ${isMobileScreen && 'create-booking-modal__content--mobile'}`}>
          <Controller
            name="additionalTitle"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <>
                <p className="input__label">{`Köster: ${userName}`}</p>
                {isPendingBooking ? (
                  <p>{value}</p>
                ) : (
                  <Input
                    placeholder={t('booking.additionalTitle')}
                    id="additional-title"
                    name={name}
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                  />
                )}
              </>
            )}
          />
          <div className={`${!isMobileScreen && 'form-row align-items-baseline'}`}>
            <Controller
              name="date"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <Input
                  label={t('booking.deliveryDate')}
                  id="delivery-date"
                  className="create-booking-modal__delivey-date"
                  name={name}
                  type="date"
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  required
                  error={errors.date?.message || (errorInputName === 'date' ? 'error' : undefined)}
                  disabled={isPendingBooking}
                />
              )}
            />

            <Controller
              name="startTime"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <Select
                  onChange={(option) => {
                    if (Array.isArray(option)) {
                      [option] = option; // eslint-disable-line no-param-reassign
                    }
                    onChange(option.value);
                    clearErrors();
                  }}
                  options={timeOptions}
                  name={name}
                  showSearch={false}
                  value={value}
                  id="deliveryTimeFrom"
                  label={t('booking.deliveryTime.from')}
                  addPlaceholderOption
                  required
                />
              )}
            />
            <Controller
              name="endTime"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <Select
                  onChange={(option) => {
                    if (Array.isArray(option)) {
                      [option] = option; // eslint-disable-line no-param-reassign
                    }
                    onChange(option.value);
                    clearErrors();
                  }}
                  options={timeOptions}
                  name={name}
                  showSearch={false}
                  value={value}
                  id="deliveryTimeTo"
                  label={t('booking.deliveryTime.to')}
                  addPlaceholderOption
                  required
                />
              )}
            />
          </div>
          <span className="create-booking-modal__delivery-time-error">{errors.endTime?.message || errors.startTime?.message}</span>
          {isPendingBooking ? (
            <Controller
              name="contactPersonId"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <Select
                  onChange={(option) => {
                    if (Array.isArray(option)) {
                      [option] = option; // eslint-disable-line no-param-reassign
                    }
                    onChange(option.value);
                    setValue('contactPersonText', option.text);
                    clearErrors();
                  }}
                  options={contactOptions}
                  name={name}
                  showSearch={false}
                  value={value}
                  id="contactPersonId"
                  label={t('common.contactPerson')}
                  addPlaceholderOption={false}
                  required
                />
              )}
            />
          ) : (
            <Controller
              name="contactPersonText"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <Input
                  label={t('booking.contactPersonSubcontractor')}
                  placeholder={t('booking.contactPersonPlaceholder')}
                  id="contact-person"
                  name={name}
                  type="text"
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  required
                />
              )}
            />
          )}
          <TransportDeliveryZone
            defaultDeliveryZoneId={initialValues.deliveryZoneId}
            defaultTransportEquipmentId={initialValues.transportEquipmentId}
            deliveryZones={deliveryZones}
            transportEquipment={transportEquipment}
            control={control}
            setValue={setValue}
          />
          <Controller
            name="description"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <TextArea
                label={t('booking.description')}
                placeholder={t('common.inputPlaceholder')}
                id="delivery-item"
                name={name}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required
              />
            )}
          />
        </div>
      </form>
    </Modal>
  );
};

export default ManageBookingInternalModal;
